import { flatMap, startCase } from "lodash";
import { useNavigate } from "react-router-dom";
import { Avatar, Box, Grid2 as Grid, TableBody, TableCell, TableRow, Tooltip } from "@mui/material";
import { getComparator, stableSort } from "../../../../custom-components/table-helper/helperTable";
import { Order, ProgressStatus } from "../../../../../../typings/types";
import { useAuth } from "../../../../../context/AuthContext";
import { tokens } from "@utils/tokens";

interface Props {
  rows: any[];
  order: Order;
  orderBy: string;
}

export function StudentProgressTableRows({ rows, order, orderBy }: Props) {
  const navigate = useNavigate();

  const { baseRole: role } = useAuth();

  const getBgcolor = (label: ProgressStatus) =>
    label === "completed" ? tokens.brandGreen : label === "remaining" ? tokens.brandBlue : tokens.brandYellow;

  const ProgressStatus = ({ label }: { label: ProgressStatus }) => {
    const bgcolor =
      label === "completed" ? tokens.brandGreen : label === "remaining" ? tokens.brandBlue : tokens.brandYellow;

    return (
      <Tooltip title={startCase(label)} placement="top-start">
        <Grid sx={{ height: "20px", width: "20px", bgcolor }} borderRadius={1} />
      </Tooltip>
    );
  };
  return (
    <>
      <TableBody>
        {stableSort(rows, getComparator(order, orderBy))?.map((row: any) => {
          return (
            <TableRow
              hover
              role="checkbox"
              tabIndex={-1}
              key={row.id}
              className="pointer"
              onClick={() => navigate(`/${role}/students/${row.id}`)}
            >
              {/* Name lives under the `attributes.*` envelope the metrics hook
                  returns — the flat `row.first_name` read below resolved to
                  undefined and rendered "Undefined Undefined". */}
              <TableCell align="left" sx={{ minWidth: "250px" }}>
                <Grid container sx={{ alignItems: "center", flexWrap: "nowrap" }} spacing={2}>
                  <Avatar
                    alt={row?.attributes?.first_name}
                    src={row?.attributes?.profile_url?.url}
                  />
                  <Grid>
                    {startCase(
                      `${row?.attributes?.first_name ?? ""} ${row?.attributes?.last_name ?? ""}`,
                    ).trim() || "—"}
                  </Grid>
                </Grid>
              </TableCell>

              <TableCell align="left" sx={{ minWidth: "460px" }}>
                <Grid container alignItems="center" gap={3}>
                  <Grid
                    container
                    wrap="nowrap"
                    sx={{ width: "120px", borderRadius: 1.5, overflow: "clip" }}
                  >
                    {flatMap(
                      row?.attributes?.progress,
                      (value, key) =>
                        key !== "course_wise_progress" && (
                          <Grid
                            key={key}
                            sx={{
                              bgcolor: getBgcolor(key as ProgressStatus),
                              width: `${value}%`,
                              height: "2rem",
                            }}
                          >
                            <Tooltip title={`${value}%`} placement="top-start">
                              <Box sx={{ opacity: 0, height: "100%" }}>{value}</Box>
                            </Tooltip>
                          </Grid>
                        ),
                    )}
                  </Grid>

                  <Grid container sx={{ width: "280px" }}>
                    {flatMap(
                      row?.attributes?.progress,
                      (value, key) =>
                        key !== "course_wise_progress" && (
                          <Grid key={key} size={4}>
                            <Grid container spacing={1}>
                              <ProgressStatus label={key as ProgressStatus} />

                              {/* Already a 0-100 percentage (computeChildMetrics);
                                  percentFormat would scale it by another ×100. */}
                              <Grid>{value == null ? "—" : `${value}%`}</Grid>
                            </Grid>
                          </Grid>
                        ),
                    )}
                  </Grid>
                </Grid>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </>
  );
}
